import {
  getAllConditionTaskIds,
  removeTaskFromConditions,
  hasAnyConditions,
} from '../../shared/ConditionsDropdown/utils/conditionHelpers';
import { calculateLayout } from '../../FlowchartView/layout';
import { DEFAULT_WIDTH, DEFAULT_HEIGHT } from '../../FlowchartView/TaskNode/hooks/useTaskNodeStyles';
import { getEffectiveDependencyIds } from '../../shared/nestDependencyInfo';

/** Matches default card size in Flow (DecisionNode uses 288-wide; layout fallbacks were 290×150 and were too tight vs rendered nodes). */
export const DECISION_LAYOUT_DEFAULT_WIDTH = 288;

/**
 * Tag colors live on the campaign (tags/categories definitions), not on each mission.
 * When creating a nested campaign, copy the parent's definitions so missions keep the same colors.
 */
export function inheritTagDefinitionsFromParent(parentSprint) {
  const defs = parentSprint?.tags || parentSprint?.categories || [];
  return defs.map((t) => ({ ...t }));
}

/** Union of dependency ids outside the selection (wrapper mission depends on these at parent level). */
export function collectExternalDependencyIdsForWrapper(movingTasks, movingSet) {
  const set = new Set();
  for (const t of movingTasks) {
    for (const id of getEffectiveDependencyIds(t)) {
      if (!movingSet.has(id)) set.add(id);
    }
  }
  return set;
}

export function externalDependencyIdsToDependsOn(idSet, sampleTask) {
  const arr = Array.from(idSet);
  if (arr.length === 0) return [];
  const sample = sampleTask?.dependsOn?.[0];
  if (sample && typeof sample === 'object') {
    return arr.map((id) => ({ taskId: id }));
  }
  return arr;
}

export function replaceDependsOnIdsWithWrapper(dependsOn, movingSet, wrapperId) {
  if (!Array.isArray(dependsOn)) return dependsOn;
  const seen = new Set();
  const out = [];
  for (const dep of dependsOn) {
    const depId = typeof dep === 'string' ? dep : dep?.taskId;
    const nextId = movingSet.has(depId) ? wrapperId : depId;
    if (seen.has(nextId)) continue;
    seen.add(nextId);
    if (typeof dep === 'string') {
      out.push(nextId);
    } else {
      out.push({ ...dep, taskId: nextId });
    }
  }
  return out;
}

/** Replace one dependency id with many (AND semantics for downstream tasks). */
export function expandDependsOnReplacing(dependsOn, oldId, newIds) {
  if (!Array.isArray(dependsOn)) return dependsOn;
  const newIdList = Array.isArray(newIds) ? newIds : [...newIds];
  if (newIdList.length === 0) return dependsOn;
  const out = [];
  const seen = new Set();
  for (const dep of dependsOn) {
    const depId = typeof dep === 'string' ? dep : dep?.taskId;
    if (depId === oldId) {
      for (const nid of newIdList) {
        if (seen.has(nid)) continue;
        seen.add(nid);
        if (typeof dep === 'string' || dep == null) {
          out.push(nid);
        } else {
          out.push({ ...dep, taskId: nid });
        }
      }
    } else {
      if (!seen.has(depId)) {
        seen.add(depId);
        out.push(dep);
      }
    }
  }
  return out;
}

export function buildNestedPreparedTaskForCompile(task, movingSet, anchorX, anchorY, destAnchorX, destAnchorY) {
  const offsetX = (task.x ?? 0) - anchorX;
  const offsetY = (task.y ?? 0) - anchorY;
  const newTask = {
    ...task,
    x: destAnchorX + offsetX,
    y: destAnchorY + offsetY,
  };
  newTask.dependsOn = (Array.isArray(task.dependsOn) ? task.dependsOn : []).filter((dep) => {
    const depId = typeof dep === 'string' ? dep : dep?.taskId;
    return movingSet.has(depId);
  });
  let uc = task.unlockConditions;
  if (uc) {
    for (const id of getAllConditionTaskIds(uc)) {
      if (!movingSet.has(id)) {
        uc = removeTaskFromConditions(uc, id);
      }
    }
    if (hasAnyConditions(uc)) newTask.unlockConditions = uc;
    else delete newTask.unlockConditions;
  }
  return newTask;
}

/**
 * Per-task dimensions for calculateLayout — must match what Flow uses when Tidy runs (measured DOM
 * or TaskNode defaults). Empty {} uses layout/constants 290×150, which packs nodes tighter than
 * the 320×162 cards, so saved positions looked “wrong” until the user clicked Tidy.
 */
export function buildNodeDimensionsMapForCompileLayout(tasks) {
  const map = {};
  for (const t of tasks) {
    const hasSavedSize =
      Number.isFinite(t.width) && Number.isFinite(t.height) && t.width > 0 && t.height > 0;
    if (hasSavedSize) {
      map[t.id] = { width: t.width, height: t.height };
    } else if (t.nodeType === 'decision') {
      map[t.id] = {
        width: DECISION_LAYOUT_DEFAULT_WIDTH,
        height: DEFAULT_HEIGHT,
      };
    } else {
      map[t.id] = { width: DEFAULT_WIDTH, height: DEFAULT_HEIGHT };
    }
  }
  return map;
}

/** Same layout engine and dimension inputs as Flow “Tidy” (see useFlowchartLayout + nodeDimensionsRef). */
export function applyTidyLayoutToCompiledTasks(preparedTasks, chainOrder = null) {
  if (!preparedTasks?.length) return preparedTasks;
  const nodeDimensions = buildNodeDimensionsMapForCompileLayout(preparedTasks);
  const { positions } = calculateLayout(preparedTasks, nodeDimensions, chainOrder);
  return preparedTasks.map((t) => {
    const p = positions[t.id];
    if (p && Number.isFinite(p.x) && Number.isFinite(p.y)) {
      return { ...t, x: p.x, y: p.y };
    }
    return t;
  });
}
